import React, { useEffect, useState, useCallback } from "react"
import { useDispatch, useSelector }  from "react-redux"
import { useNavigate }               from "react-router-dom"
import axios                         from "axios"
import { selectUser }                from "../../features/userSlice"
import { openTab }                   from "../../features/tabsSlice"
import { initForm }                  from "../../features/formsSlice"
import { loadScreenPrefs,
         selectScreenPrefs,
         selectUi }                  from "../../features/uiSlice"
import "./DashboardContent.css"

// ── Form defaults registry (same as AppShell) ─────────────────
const FORM_DEFAULTS = {
  customer:   { code: "", name: "", gstin: "", credit: "", phone: "", email: "" },
  salesorder: { sono: "", date: "", customer: "", amount: "", status: "Draft"  },
  purchorder: { pono: "", date: "", supplier: "", amount: "", status: "Draft"  },
  journal:    { jvno: "", date: "", narration: "", dram: "", cram: ""           },
}

// ── KPI configuration ─────────────────────────────────────────
const KPI_CONFIG = [
  {
    key:    "revenue",
    label:  "Revenue (MTD)",
    icon:   "₹",
    iconBg: "rgba(24,95,165,0.1)",
    iconC:  "#185FA5",
    format: (v) => `₹${v}`,
    endpoint: "/api/analysis/kpi/revenue",
  },
  {
    key:    "ordersToday",
    label:  "Orders Today",
    icon:   "🛒",
    iconBg: "rgba(29,158,117,0.1)",
    iconC:  "#1D9E75",
    format: (v) => String(v),
    endpoint: "/api/analysis/kpi/orders-today",
  },
  {
    key:    "pendingBills",
    label:  "Pending Bills",
    icon:   "📋",
    iconBg: "rgba(239,159,39,0.1)",
    iconC:  "#EF9F27",
    format: (v) => `₹${v}`,
    endpoint: "/api/analysis/kpi/pending-bills",
  },
  {
    key:    "stockAlerts",
    label:  "Stock Alerts",
    icon:   "⚠",
    iconBg: "rgba(226,75,74,0.1)",
    iconC:  "#E24B4A",
    format: (v) => String(v),
    endpoint: "/api/analysis/kpi/stock-alerts",
  },
]

// ── Static fallback data while API loads ──────────────────────
const KPI_FALLBACK = {
  revenue:      { val: "48.2L",  delta: "+12.4%", dir: "up",  spark: [40,55,45,70,60,85,75] },
  ordersToday:  { val: "142",    delta: "+8 new",  dir: "up",  spark: [80,95,60,110,90,130,142] },
  pendingBills: { val: "6.1L",   delta: "3 overdue",dir:"dn", spark: [50,60,55,65,60,58,61] },
  stockAlerts:  { val: "17",     delta: "Below reorder",dir:"dn",spark:[5,8,10,12,14,16,17] },
}

// ─────────────────────────────────────────────────────────────
const DashboardContent = ({ tabId }) => {
  const dispatch  = useDispatch()
  const navigate  = useNavigate()
  const user      = useSelector(selectUser)
  const ui        = useSelector(selectUi)

  // Per-screen preferences — saved to Oracle
  const screenPrefs = useSelector(selectScreenPrefs("DASHBOARD"))

  const [menuGroups,   setMenuGroups]   = useState([])
  const [recentOrders, setRecentOrders] = useState([])
  const [activityFeed, setActivityFeed] = useState([])
  const [kpiData,      setKpiData]      = useState(KPI_FALLBACK)
  const [poolStats,    setPoolStats]    = useState(null)
  const [loading,      setLoading]      = useState(true)
  const [activeTab,    setActiveTab]    = useState(
    screenPrefs?.lastActiveTab ?? 0
  )
  const [weekData,     setWeekData]     = useState([55,72,44,88,62,94,76])

  const token   = user?.token
  const emp_id  = user?.emp_id
  const headers = { Authorization: `Bearer ${token}` }

  // ── Load all dashboard data ──────────────────────────────
  useEffect(() => {
    if (!token || !emp_id) { navigate("/login", { replace: true }); return }

    // Load Oracle screen preferences for DASHBOARD
    dispatch(loadScreenPrefs("DASHBOARD"))

    const fetchAll = async () => {
      try {
        setLoading(true)
        await Promise.allSettled([
          fetchMenu(),
          fetchRecentOrders(),
          fetchActivityFeed(),
          fetchKpis(),
          fetchPoolStats(),
          fetchWeekData(),
        ])
      } finally {
        setLoading(false)
      }
    }
    fetchAll()
  }, [token, emp_id]) // eslint-disable-line

  // ── Individual fetch functions ───────────────────────────

  const fetchMenu = async () => {
    try {
      // This is your existing Oracle menu API — now uses pool
      const { data } = await axios.post(
        "/api/menu/getMenuUserData",
        { emp_id },
        { headers }
      )
      // Build parent → children tree (your existing logic)
      const parents  = data.filter(m => m.PARENT_MENU_ID === null)
      const children = data.filter(m => m.PARENT_MENU_ID !== null)

      const grouped = parents.map(p => ({
        group:    p.MENU_NAME,
        menuId:   p.MENU_ID,
        icon:     MENU_ICONS[p.MENU_NAME] || "📄",
        tiles: children
          .filter(c => c.PARENT_MENU_ID === p.MENU_ID)
          .map(c => ({
            name:   c.MENU_NAME,
            url:    c.MENU_URL,
            formId: urlToFormId(c.MENU_URL),
            icon:   TILE_ICONS[c.MENU_NAME] || "📄",
            bg:     TILE_BG[p.MENU_NAME]    || "rgba(24,95,165,0.1)",
            count:  null,  // optionally fetch counts per tile
          }))
      })).filter(g => g.tiles.length > 0)

      setMenuGroups(grouped)
    } catch (err) {
      console.error("fetchMenu error:", err)
    }
  }

  const fetchRecentOrders = async () => {
    try {
      const { data } = await axios.get(
        "/api/order/recent?limit=5",
        { headers }
      )
      setRecentOrders(data)
    } catch (err) {
      // Fallback static data — dashboard still usable if this fails
      setRecentOrders(STATIC_ORDERS)
    }
  }

  const fetchActivityFeed = async () => {
    try {
      const { data } = await axios.get(
        "/api/analysis/activity?limit=6",
        { headers }
      )
      setActivityFeed(data)
    } catch {
      setActivityFeed(STATIC_ACTIVITY)
    }
  }

  const fetchKpis = async () => {
    // Fire all KPI calls in parallel — each uses pool connection
    const results = await Promise.allSettled(
      KPI_CONFIG.map(k =>
        axios.get(k.endpoint, { headers })
          .then(r => ({ key: k.key, ...r.data }))
      )
    )
    const updated = { ...KPI_FALLBACK }
    results.forEach(r => {
      if (r.status === "fulfilled") updated[r.value.key] = r.value
    })
    setKpiData(updated)
  }

  const fetchPoolStats = async () => {
    try {
      const { data } = await axios.get("/api/health/db", { headers })
      setPoolStats(data)
    } catch { /* non-critical */ }
  }

  const fetchWeekData = async () => {
    try {
      const { data } = await axios.get(
        "/api/analysis/revenue-week",
        { headers }
      )
      if (Array.isArray(data)) setWeekData(data)
    } catch { /* use default sparkline */ }
  }

  // ── Open form in MDI tab ─────────────────────────────────
  const handleOpenTab = useCallback((formId, formType, label) => {
    if (FORM_DEFAULTS[formType]) {
      dispatch(initForm({ tabId: formId, formType, defaults: FORM_DEFAULTS[formType] }))
    }
    dispatch(openTab({ id: formId, formType, label }))
  }, [dispatch])

  const handleTileClick = useCallback((tile) => {
    if (!tile.formId) return
    handleOpenTab(tile.formId, tile.formId, tile.name)
  }, [handleOpenTab])

  // ── Tab change — save to Oracle screen prefs ─────────────
  const handleTabChange = (idx) => {
    setActiveTab(idx)
    dispatch({
      type:    "ui/setScreenPref",
      payload: { screenKey: "DASHBOARD", key: "lastActiveTab", value: idx }
    })
  }

  const DASH_TABS = ["Overview", "Sales Activity", "Finance Summary", "Alerts"]

  // ── Greeting ─────────────────────────────────────────────
  const hour     = new Date().getHours()
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening"
  const userName = user?.Name || user?.emp_id || "User"

  if (loading) return <DashboardSkeleton />

  return (
    <div className="dash-root" style={{ fontFamily: `var(--erp-font, 'IBM Plex Sans', system-ui)` }}>

      {/* ── WELCOME STRIP ── */}
      <WelcomeStrip
        greeting={greeting}
        userName={userName}
        user={user}
        poolStats={poolStats}
      />

      {/* ── KPI CARDS ── */}
      <div className="kpi-grid">
        {KPI_CONFIG.map(cfg => (
          <KpiCard
            key={cfg.key}
            config={cfg}
            data={kpiData[cfg.key]}
          />
        ))}
      </div>

      {/* ── TABS ── */}
      <DashTabs
        tabs={DASH_TABS}
        active={activeTab}
        onChange={handleTabChange}
      />

      {/* ── MAIN CONTENT (tab-driven) ── */}
      {activeTab === 0 && (
        <OverviewTab
          recentOrders={recentOrders}
          activityFeed={activityFeed}
          weekData={weekData}
          menuGroups={menuGroups}
          poolStats={poolStats}
          onTileClick={handleTileClick}
          onOrderClick={(order) =>
            handleOpenTab("salesorder", "salesorder", "Sales Order")
          }
        />
      )}

      {activeTab === 1 && (
        <SalesActivityTab
          recentOrders={recentOrders}
          onOrderClick={(order) =>
            handleOpenTab("salesorder", "salesorder", "Sales Order")
          }
        />
      )}

      {activeTab === 2 && (
        <FinanceSummaryTab headers={headers} />
      )}

      {activeTab === 3 && (
        <AlertsTab headers={headers} />
      )}

    </div>
  )
}

// ── SUB-COMPONENTS ────────────────────────────────────────────

const WelcomeStrip = ({ greeting, userName, user, poolStats }) => (
  <div className="welcome-strip">
    <div>
      <div className="welcome-greet">{greeting}, {userName} 👋</div>
      <div className="welcome-sub">
        {user?.entity || "Singhal Industries"} ·{" "}
        {user?.division || "Mumbai HO"} ·{" "}
        FY {new Date().getFullYear()}-{String(new Date().getFullYear() + 1).slice(2)}
      </div>
    </div>
    <div className="welcome-right">
      <div className="info-pill">
        <div className="live-dot" />
        Oracle Pool · {poolStats
          ? `${poolStats.connectionsOpen}/20 · ${poolStats.avgMs ?? 2}ms avg`
          : "Connected"}
      </div>
      <div className="info-pill">
        📅 {new Date().toLocaleDateString("en-IN", {
          weekday: "short", day: "numeric",
          month: "short", year: "numeric"
        })}
      </div>
    </div>
  </div>
)

const KpiCard = ({ config, data }) => {
  const maxSpark = Math.max(...(data?.spark ?? [1]))
  return (
    <div className="kpi-card">
      <div className="kpi-card-top">
        <div className="kpi-label">{config.label}</div>
        <div className="kpi-icon" style={{ background: config.iconBg, color: config.iconC }}>
          {config.icon}
        </div>
      </div>
      <div className="kpi-value">{data ? config.format(data.val) : "—"}</div>
      <div className={`kpi-delta ${data?.dir === "up" ? "kup" : data?.dir === "dn" ? "kdn" : "knt"}`}>
        {data?.delta}
      </div>
      {data?.spark && (
        <div className="kpi-sparkline">
          {data.spark.map((h, i) => (
            <div
              key={i}
              className="spark-bar"
              style={{
                height: `${Math.round(h / maxSpark * 100)}%`,
                background: config.iconBg,
                borderTopColor: config.iconC,
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const DashTabs = ({ tabs, active, onChange }) => (
  <div className="dash-tabs">
    {tabs.map((t, i) => (
      <button
        key={t}
        className={`dash-tab ${i === active ? "cur" : ""}`}
        onClick={() => onChange(i)}
      >
        {t}
      </button>
    ))}
  </div>
)

// ── OVERVIEW TAB ─────────────────────────────────────────────
const OverviewTab = ({
  recentOrders, activityFeed, weekData,
  menuGroups, poolStats, onTileClick, onOrderClick,
}) => (
  <>
    <div className="grid-2-3">

      {/* Recent Orders */}
      <div className="card">
        <div className="card-hdr">
          Recent Sales Orders
          <span className="badge bb">Live</span>
        </div>
        <div className="card-body-np">
          <table className="data-table">
            <thead>
              <tr>
                <th>Order No</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.map((o, i) => (
                <tr key={i} onClick={() => onOrderClick(o)}>
                  <td className="mono">{o.SO_NO   || o.sono}</td>
                  <td className="bold">{o.CUSTOMER || o.customer}</td>
                  <td className="mono">{o.DATE     || o.date}</td>
                  <td className="mono">{o.AMOUNT   || o.amount}</td>
                  <td><StatusBadge status={o.STATUS || o.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <button className="link-btn">View all orders →</button>
        </div>
      </div>

      {/* Right column */}
      <div className="right-col">
        {/* Weekly chart */}
        <div className="card">
          <div className="card-hdr">Revenue — This Week</div>
          <div className="card-body">
            <WeeklyBarChart data={weekData} />
          </div>
        </div>
        {/* Quick stats */}
        <div className="card">
          <div className="card-hdr">Alerts & Pending</div>
          <QuickStats />
        </div>
      </div>
    </div>

    {/* Activity + Menu Tiles */}
    <div className="grid-3-parts">
      {/* Menu tiles — from Oracle menu API */}
      <div className="tiles-host">
        {menuGroups.map(group => (
          <div key={group.menuId ?? group.group} className="tile-section">
            <div className="tile-section-hdr">
              <span className="tile-section-title">{group.group}</span>
              <div className="tile-section-bar" />
            </div>
            <div className="tiles-grid">
              {group.tiles.map(tile => (
                <div
                  key={tile.url}
                  className="tile"
                  onClick={() => onTileClick(tile)}
                >
                  <div className="tile-icon" style={{ background: tile.bg }}>
                    {tile.icon}
                  </div>
                  <div className="tile-name">{tile.name}</div>
                  {tile.count != null && (
                    <div className={`tile-count ${tile.isHot ? "hot" : ""}`}>
                      {tile.count}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Activity Feed */}
      <div className="card">
        <div className="card-hdr">
          Activity Feed
          <div className="live-dot" />
        </div>
        <div className="card-body-np">
          {activityFeed.map((item, i) => (
            <div key={i} className="activity-item">
              <div
                className="activity-dot"
                style={{ background: item.color || ACTIVITY_COLORS[i % ACTIVITY_COLORS.length] }}
              />
              <span className="activity-text">
                {item.ACTIVITY_TEXT || item.text}
              </span>
              <span className="activity-time">
                {item.ACTIVITY_TIME || item.time}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Pool + Fiscal Info */}
      <div className="info-col">
        <PoolStatsCard poolStats={poolStats} />
        <FiscalCard user={{}} />
      </div>
    </div>
  </>
)

// ── SALES ACTIVITY TAB ────────────────────────────────────────
const SalesActivityTab = ({ recentOrders, onOrderClick }) => {
  const [data, setData] = useState(null)

  useEffect(() => {
    // Lazy load — only fetches when this tab is opened
    axios.get("/api/analysis/sales-summary")
      .then(r => setData(r.data))
      .catch(() => setData(null))
  }, [])

  return (
    <div className="tab-content">
      <div className="grid-2-equal">
        <div className="card">
          <div className="card-hdr">All Recent Orders</div>
          <div className="card-body-np">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order No</th><th>Customer</th>
                  <th>Amount</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((o, i) => (
                  <tr key={i} onClick={() => onOrderClick(o)}>
                    <td className="mono">{o.SO_NO     || o.sono}</td>
                    <td className="bold">{o.CUSTOMER   || o.customer}</td>
                    <td className="mono">{o.AMOUNT     || o.amount}</td>
                    <td><StatusBadge status={o.STATUS || o.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <div className="card-hdr">Top Customers (MTD)</div>
          <div className="card-body">
            {(data?.topCustomers || STATIC_TOP_CUSTOMERS).map((c, i) => (
              <div key={i} className="rank-row">
                <span className="rank-num">{i + 1}</span>
                <span className="rank-name">{c.name}</span>
                <span className="rank-val">{c.amount}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── FINANCE SUMMARY TAB ───────────────────────────────────────
const FinanceSummaryTab = ({ headers }) => {
  const [data, setData] = useState(null)

  useEffect(() => {
    axios.get("/api/analysis/finance-summary", { headers })
      .then(r => setData(r.data))
      .catch(() => setData(null))
  }, []) // eslint-disable-line

  return (
    <div className="tab-content">
      <div className="grid-r4">
        {[
          { lbl: "Total Receivable",  val: data?.receivable  || "₹18.4L", c: "#185FA5" },
          { lbl: "Total Payable",     val: data?.payable     || "₹9.2L",  c: "#EF9F27" },
          { lbl: "Bank Balance",      val: data?.bankBalance || "₹32.1L", c: "#1D9E75" },
          { lbl: "Overdue (> 30d)",   val: data?.overdue     || "₹3.6L",  c: "#E24B4A" },
        ].map(s => (
          <div key={s.lbl} className="kpi-card">
            <div className="kpi-label">{s.lbl}</div>
            <div className="kpi-value" style={{ color: s.c }}>{s.val}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── ALERTS TAB ────────────────────────────────────────────────
const AlertsTab = ({ headers }) => {
  const [alerts, setAlerts] = useState(null)

  useEffect(() => {
    axios.get("/api/analysis/alerts", { headers })
      .then(r => setAlerts(r.data))
      .catch(() => setAlerts(STATIC_ALERTS))
  }, []) // eslint-disable-line

  return (
    <div className="tab-content">
      <div className="card">
        <div className="card-hdr">Active Alerts</div>
        <div className="card-body-np">
          {(alerts || STATIC_ALERTS).map((a, i) => (
            <div key={i} className="alert-row">
              <span className={`alert-dot ${a.severity}`} />
              <div className="alert-body">
                <div className="alert-title">{a.title}</div>
                <div className="alert-desc">{a.description}</div>
              </div>
              <span className={`badge ${a.badgeClass}`}>{a.severity}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── SMALL COMPONENTS ─────────────────────────────────────────

const WeeklyBarChart = ({ data }) => {
  const max = Math.max(...data)
  const days = ["M", "T", "W", "T", "F", "S", "S"]
  return (
    <div>
      <div className="bar-chart">
        {data.map((h, i) => (
          <div
            key={i}
            className="b-bar"
            style={{ height: `${Math.round(h / max * 100)}%` }}
          />
        ))}
      </div>
      <div className="bar-labels">
        {days.map((d, i) => <span key={i} className="bar-lbl">{d}</span>)}
      </div>
    </div>
  )
}

const QuickStats = () => (
  <div>
    {[
      { lbl: "Overdue Invoices",    val: "3",  cls: "br" },
      { lbl: "Low Stock Items",     val: "17", cls: "bw" },
      { lbl: "Pending POs",         val: "8",  cls: "bb" },
      { lbl: "Unposted JVs",        val: "5",  cls: "bn" },
      { lbl: "Open Requisitions",   val: "4",  cls: "bb" },
    ].map(q => (
      <div key={q.lbl} className="quick-stat">
        <span className="quick-stat-lbl">{q.lbl}</span>
        <span className={`badge ${q.cls}`}>{q.val}</span>
      </div>
    ))}
  </div>
)

const PoolStatsCard = ({ poolStats }) => (
  <div className="card">
    <div className="card-hdr">Oracle Pool Health</div>
    <div className="card-body">
      {[
        ["Connections Open", poolStats?.connectionsOpen  ?? 4,    "#1D9E75"],
        ["In Use",           poolStats?.connectionsInUse ?? 1,    "#185FA5"],
        ["Available",        (poolStats?.connectionsOpen ?? 4) -
                             (poolStats?.connectionsInUse ?? 1),  "#1D9E75"],
        ["Pool Max",         poolStats?.poolMax          ?? 20,   "#888"   ],
        ["Avg Response",     `${poolStats?.avgMs ?? 2}ms`,        "#1D9E75"],
      ].map(([lbl, val, c]) => (
        <div key={lbl} className="info-row">
          <span className="info-lbl">{lbl}</span>
          <span className="info-val mono" style={{ color: c }}>{val}</span>
        </div>
      ))}
    </div>
  </div>
)

const FiscalCard = ({ user }) => (
  <div className="card">
    <div className="card-hdr">Fiscal Info</div>
    <div className="card-body">
      {[
        ["Year",    `${new Date().getFullYear()}-${String(new Date().getFullYear()+1).slice(2)}`],
        ["Quarter", "Q1"],
        ["Period",  new Date().toLocaleDateString("en-IN",{month:"long",year:"numeric"})],
        ["Entity",  user?.entity   || "SING-MUM"],
        ["Division",user?.division || "MUM-HO"  ],
      ].map(([lbl, val]) => (
        <div key={lbl} className="info-row">
          <span className="info-lbl">{lbl}</span>
          <span className="info-val mono">{val}</span>
        </div>
      ))}
    </div>
  </div>
)

const StatusBadge = ({ status }) => {
  const cls =
    status === "Approved" || status === "Shipped" ? "bg" :
    status === "Pending"                          ? "bw" :
    status === "On Hold"  || status === "Rejected"? "br" : "bn"
  return <span className={`badge ${cls}`}>{status}</span>
}

const DashboardSkeleton = () => (
  <div className="dash-root">
    <div className="kpi-grid">
      {[1,2,3,4].map(i => (
        <div key={i} className="kpi-card">
          <div className="skeleton" style={{ height: 10, width: "60%", marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 24, width: "80%", marginBottom: 6 }} />
          <div className="skeleton" style={{ height: 8,  width: "50%" }} />
        </div>
      ))}
    </div>
    <div className="skeleton" style={{ height: 200, borderRadius: 6, marginBottom: 10 }} />
    <div className="skeleton" style={{ height: 140, borderRadius: 6 }} />
  </div>
)

// ── Static fallback data ──────────────────────────────────────
const STATIC_ORDERS = [
  { SO_NO: "SO-0041", CUSTOMER: "Tata Industries",  DATE: "14 Apr", AMOUNT: "₹1,24,500", STATUS: "Approved" },
  { SO_NO: "SO-0040", CUSTOMER: "Reliance Corp",    DATE: "14 Apr", AMOUNT: "₹82,000",   STATUS: "Pending"  },
  { SO_NO: "SO-0039", CUSTOMER: "Infosys Ltd",      DATE: "13 Apr", AMOUNT: "₹3,45,000", STATUS: "Shipped"  },
  { SO_NO: "SO-0038", CUSTOMER: "Wipro Ltd",        DATE: "12 Apr", AMOUNT: "₹56,750",   STATUS: "On Hold"  },
  { SO_NO: "SO-0037", CUSTOMER: "HCL Technologies", DATE: "12 Apr", AMOUNT: "₹98,200",   STATUS: "Approved" },
]
const STATIC_ACTIVITY = [
  { text: "SO-0041 approved by Ramesh Kumar",        time: "09:24", color: "#185FA5" },
  { text: "Payment ₹82,000 received — Reliance",     time: "08:50", color: "#1D9E75" },
  { text: "Low stock: Industrial Valve 2\" (12 left)",time: "08:30", color: "#EF9F27" },
  { text: "SO-0038 on hold — credit limit exceeded", time: "08:15", color: "#D85A30" },
  { text: "PO-0018 created for Ashok Suppliers",     time: "07:55", color: "#185FA5" },
  { text: "INV-0234 posted — Tata Industries",       time: "07:40", color: "#1D9E75" },
]
const STATIC_ALERTS = [
  { title: "3 Overdue Invoices",      description: "INV-0220, INV-0218, INV-0214 overdue > 30 days", severity: "high",   badgeClass: "br" },
  { title: "Low Stock — 17 Items",    description: "Industrial Valve 2\" and 16 others below reorder level", severity: "medium", badgeClass: "bw" },
  { title: "GST Return Due",          description: "GSTR-1 for March 2026 due by 11 Apr", severity: "high",   badgeClass: "br" },
  { title: "8 Pending Purchase Orders",description: "Awaiting approval from purchase committee", severity: "low",    badgeClass: "bb" },
]
const STATIC_TOP_CUSTOMERS = [
  { name: "Tata Industries",  amount: "₹12.4L" },
  { name: "Reliance Corp",    amount: "₹9.8L"  },
  { name: "Infosys Ltd",      amount: "₹8.2L"  },
  { name: "HCL Technologies", amount: "₹6.5L"  },
  { name: "Wipro Ltd",        amount: "₹4.9L"  },
]
const ACTIVITY_COLORS = ["#185FA5","#1D9E75","#EF9F27","#D85A30","#534AB7","#1D9E75"]

// ── Menu icon maps (matches your Oracle MENU_NAME values) ─────
const MENU_ICONS = {
  Sales:      "🛒",  Purchase: "📦",
  Finance:    "💰",  Inventory:"🏭",
  Reports:    "📊",  HR:       "👥",
  Production: "⚙",  System:   "🔧",
}
const TILE_ICONS = {
  "Quotation":           "📝",
  "Sales Order":         "🛒",
  "Delivery Note":       "🚚",
  "Sales Invoice":       "🧾",
  "Sales Return":        "↩",
  "Purchase Requisition":"📋",
  "Purchase Order":      "📦",
  "GRN":                 "✅",
  "Purchase Invoice":    "🧾",
  "Receipt Voucher":     "💰",
  "Payment Voucher":     "💳",
  "Journal Entry":       "📒",
  "Debit Note":          "📤",
  "Credit Note":         "📥",
  "Trial Balance":       "⚖",
  "P&L Statement":       "📈",
  "GST Returns":         "🏛",
  "Sales Reports":       "📊",
}
const TILE_BG = {
  Sales:    "rgba(24,95,165,0.1)",
  Purchase: "rgba(239,159,39,0.1)",
  Finance:  "rgba(29,158,117,0.1)",
  Inventory:"rgba(83,74,183,0.1)",
  Reports:  "rgba(83,74,183,0.1)",
}

// ── Map MENU_URL → formId used by tabsSlice ───────────────────
const urlToFormId = (url = "") =>
  url.replace(/^\//, "").replace(/\//g, "-").toLowerCase() || "unknown"

export default DashboardContent